// THE PRODUCT'S NAME AND MARK (configurable identity P1-12, 2026-10-29).
//
// Two layers, and the order between them is the whole design:
//
//   1. BUILD TIME -- the brand pack (src/brands/<id>/, chosen with
//      REACT_APP_BRAND, "default" when unset): the name, the mark and the
//      favicons. Everything shown BEFORE anyone is signed in uses only this,
//      because the settings are not readable until then.
//   2. RUNTIME -- Settings > Company "Product name". When set, it replaces the
//      pack's name on the signed-in screens; EMPTY means "use the pack's".
//
// ⚠️ With the default pack and the stored value live already holds ("CPQ
// Lite"), both layers say the same thing, so nothing anyone sees changes.
// This is DISPLAY ONLY: no figure, request or rule reads it.

import { useSyncExternalStore } from "react";
import pack from "@brand/brand.json";
import logo from "@brand/logo.svg";

/** The build's brand: what the sign-in screen and the page title start with. */
export const BRAND = {
  name: pack.name as string,
  /** Accessible name of the mark. Not visible text. */
  markAlt: pack.markAlt as string,
  logo: logo as string,
};

let productName: string = BRAND.name;
const listeners = new Set<() => void>();

export function getProductName(): string {
  return productName;
}

/** Apply Settings > Company's product name. Blank keeps / restores the build's
 *  own name. A no-op when nothing changes, so the default path never touches
 *  the document title. */
export function setProductName(name: string | null | undefined): void {
  const next = (name ?? "").trim() || BRAND.name;
  if (next === productName) return;
  productName = next;
  if (typeof document !== "undefined") document.title = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The product name for the signed-in screens; re-renders when it changes. */
export function useProductName(): string {
  return useSyncExternalStore(subscribe, getProductName, getProductName);
}
